import React from 'react';
import { useSelector } from 'react-redux';
import { PslfStatus, StudentLoanPlanResult } from 'api/StudentLoanApi';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import {
  Checklist,
  DetailsCard,
  InfoNote,
  PaymentSummary,
  SavingsHeadline,
} from 'components/StudentLoanForgiveness/Common/ResultBlocks';
import { getFindingCopy } from 'components/StudentLoanForgiveness/studentLoanCopy';
import {
  formatPayment,
  getComparisonRows,
  getPathSummaryRows,
  getSavingsLabel,
  StudentLoanRow,
} from 'components/StudentLoanForgiveness/studentLoanFormat';
import { PlanScreenResult } from 'enums/StudentLoanForgivenessFlowResults';
import { FlowComponentType } from 'routes/types';
import { getStudentLoanData, getStudentLoanPlanResult } from 'selectors/getStudentLoanData';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { ReactComponent as LockOpenIcon } from 'images/lock-open.svg';

import styles from './Plan.module.scss';

const pslfStatusLabels: Record<PslfStatus, string> = {
  [PslfStatus.Likely]: 'Likely eligible',
  [PslfStatus.Unconfirmed]: 'Not confirmed yet',
  [PslfStatus.NotEligible]: 'Not eligible',
};

const getFileRows = ({ balance, current, pslf }: StudentLoanPlanResult): StudentLoanRow[] => [
  { label: 'Balance', value: formatMonetaryAmount(balance) },
  { label: 'Current plan', value: `${current.label}, ${formatPayment(current.firstMonthlyPayment)}/mo` },
  { label: 'PSLF', value: pslfStatusLabels[pslf.status], isStrong: true },
];

const Plan = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const planResult = getStudentLoanPlanResult(useSelector(getStudentLoanData));
  const savingsLabel = planResult ? getSavingsLabel(planResult) : null;

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        {planResult && (
          <FormContainer
            icon={<LockOpenIcon />}
            title={savingsLabel ? 'Your plan' : "You're on a good path"}
            subtitle="Built from your studentaid.gov file and your answers."
          >
            {savingsLabel && <SavingsHeadline amount={savingsLabel} />}

            <DetailsCard title="What your file shows" rows={getFileRows(planResult)} />

            {planResult.findings.map((finding) => (
              <InfoNote key={finding.code} {...getFindingCopy(finding, { basis: planResult.basis })} />
            ))}

            <PaymentSummary
              amount={formatPayment(planResult.recommended.firstMonthlyPayment)}
              rows={getPathSummaryRows(planResult.recommended)}
            />

            <DetailsCard title="Compared with staying on your current plan" rows={getComparisonRows(planResult)} />

            {planResult.statePrograms.length > 0 && (
              <div className={styles.programs}>
                <p className={styles.programsTitle}>State and service programs</p>
                <p className={styles.programNote}>
                  These are conditional if awarded. They are not included in your savings.
                </p>
                {planResult.statePrograms.map((program) => (
                  <div key={program.id} className={styles.program}>
                    <a href={program.applyUrl} target="_blank" rel="noreferrer">
                      {program.name}
                    </a>
                    <p className={styles.programNote}>{program.summary}</p>
                  </div>
                ))}
              </div>
            )}

            {planResult.dependsOn.length > 0 && (
              <Checklist title="This estimate depends on" items={planResult.dependsOn} />
            )}

            {planResult.steps.length > 0 ? (
              <Button onClick={() => handleNext(PlanScreenResult.SeeSteps)}>See my steps</Button>
            ) : (
              <Button onClick={() => handleNext(PlanScreenResult.Done)}>Done</Button>
            )}
          </FormContainer>
        )}
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Plan;
